import { useCallback, useEffect, useRef, useState } from 'react'
import { Maximize2, Pause, Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WALKTHROUGHS, formatClock, stepAt } from '@/help/walkthroughs'

const SPEEDS = [0.75, 1, 1.5, 2] as const

/**
 * A recorded walkthrough of the real app, with its steps as chapters.
 *
 * Plays muted and inline once it scrolls into view, so a reader sees the motion
 * without pressing anything — except under `prefers-reduced-motion`, where it
 * waits for Play. It pauses again when scrolled away. Every step is a chapter:
 * a tick on the progress bar and a row in the list underneath, and either one
 * seeks there, so a reader can jump straight to the part they need.
 */
export function WalkthroughPlayer({ name }: { name: string }) {
  const wt = WALKTHROUGHS[name]
  const video = useRef<HTMLVideoElement>(null)
  const frame = useRef<HTMLDivElement>(null)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [speed, setSpeed] = useState<number>(1)
  const [ended, setEnded] = useState(false)
  const userPaused = useRef(false)

  useEffect(() => {
    const el = video.current
    if (!el || !wt) return
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          if (!userPaused.current && el.paused && !el.ended) el.play().catch(() => {})
        } else if (!el.paused) {
          el.pause()
        }
      },
      { threshold: [0, 0.6] },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [wt])

  const seek = useCallback((t: number) => {
    const el = video.current
    if (!el) return
    el.currentTime = t
    setTime(t)
    setEnded(false)
    userPaused.current = false
    el.play().catch(() => {})
  }, [])

  const toggle = useCallback(() => {
    const el = video.current
    if (!el) return
    if (el.paused) {
      userPaused.current = false
      if (el.ended) el.currentTime = 0
      el.play().catch(() => {})
    } else {
      userPaused.current = true
      el.pause()
    }
  }, [])

  if (!wt) {
    return (
      <pre className='my-6 overflow-x-auto rounded-lg border bg-muted/40 p-4 text-xs text-muted-foreground'>
        Walkthrough not recorded yet: {name}
      </pre>
    )
  }

  const current = stepAt(wt.steps, time)
  const progress = wt.duration > 0 ? Math.min(1, time / wt.duration) : 0
  // A portrait recording (the mobile app) is shown phone-sized and centred,
  // rather than stretched to the column's width and towering over the page.
  const portrait = wt.height > wt.width

  return (
    <figure className='walkthrough not-prose my-6 overflow-hidden rounded-xl border bg-card shadow-sm'>
      <div ref={frame} className={cn('group relative bg-slate-950', portrait && 'flex justify-center py-4')}>
        <video
          ref={video}
          src={wt.videoUrl}
          poster={wt.posterUrl}
          muted
          playsInline
          preload='metadata'
          aria-label={`Walkthrough: ${wt.title}`}
          style={{ aspectRatio: `${wt.width} / ${wt.height}` }}
          className={cn(
            'block cursor-pointer',
            portrait ? 'h-[min(640px,75vh)] w-auto rounded-2xl ring-4 ring-slate-800' : 'w-full',
          )}
          onClick={toggle}
          onPlay={() => {
            setPlaying(true)
            setEnded(false)
          }}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false)
            setEnded(true)
          }}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onRateChange={(e) => setSpeed(e.currentTarget.playbackRate)}
        />
        {!playing && (
          <button
            type='button'
            onClick={toggle}
            aria-label={ended ? 'Replay walkthrough' : 'Play walkthrough'}
            className='absolute inset-0 flex items-center justify-center bg-slate-950/25 transition hover:bg-slate-950/35'
          >
            <span className='flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-xl'>
              {ended ? <RotateCcw className='h-7 w-7' /> : <Play className='ml-1 h-7 w-7' />}
            </span>
          </button>
        )}
      </div>

      <div className='flex items-center gap-3 border-t px-3 py-2'>
        <button
          type='button'
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-muted'
        >
          {playing ? <Pause className='h-4 w-4' /> : <Play className='h-4 w-4' />}
        </button>

        <div
          role='slider'
          tabIndex={0}
          aria-label='Seek'
          aria-valuemin={0}
          aria-valuemax={Math.round(wt.duration)}
          aria-valuenow={Math.round(time)}
          className='relative h-2 flex-1 cursor-pointer rounded-full bg-muted'
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            seek(((e.clientX - r.left) / r.width) * wt.duration)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') seek(Math.min(wt.duration, time + 5))
            if (e.key === 'ArrowLeft') seek(Math.max(0, time - 5))
          }}
        >
          <div className='absolute inset-y-0 left-0 rounded-full bg-primary' style={{ width: `${progress * 100}%` }} />
          {wt.steps.map((s, i) => (
            <span
              key={i}
              title={`${i + 1}. ${s.title}`}
              className='absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded bg-background/90'
              style={{ left: `${(s.t / wt.duration) * 100}%` }}
            />
          ))}
        </div>

        <span className='w-20 shrink-0 text-right font-mono text-xs text-muted-foreground'>
          {formatClock(time)} / {formatClock(wt.duration)}
        </span>

        <button
          type='button'
          onClick={() => {
            const next = SPEEDS[(SPEEDS.indexOf(speed as (typeof SPEEDS)[number]) + 1) % SPEEDS.length]
            if (video.current) video.current.playbackRate = next
          }}
          title='Playback speed'
          className='h-8 shrink-0 rounded-md px-2 font-mono text-xs hover:bg-muted'
        >
          {speed}×
        </button>
        <button
          type='button'
          onClick={() => frame.current?.requestFullscreen?.()}
          aria-label='Fullscreen'
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-muted'
        >
          <Maximize2 className='h-4 w-4' />
        </button>
      </div>

      <ol className='grid gap-0.5 border-t bg-muted/30 p-2 sm:grid-cols-2'>
        {wt.steps.map((s, i) => (
          <li key={i}>
            <button
              type='button'
              onClick={() => seek(s.t)}
              aria-current={i === current ? 'step' : undefined}
              className={cn(
                'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] leading-snug transition',
                i === current ? 'bg-primary/10 text-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'mt-px flex h-5 min-w-5 items-center justify-center rounded px-1 text-[11px] font-semibold',
                  i === current ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/15',
                )}
              >
                {i + 1}
              </span>
              <span className='flex-1'>{s.title}</span>
              <span className='font-mono text-[11px] opacity-60'>{formatClock(s.t)}</span>
            </button>
          </li>
        ))}
      </ol>
      <figcaption className='sr-only'>{wt.title}</figcaption>
    </figure>
  )
}
